import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { FormRow, Select, TextField } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { can } from "@src/utils/components/permissions";
import {
  fieldToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FunctionComponent, useContext, useEffect } from "react";
import { FormCreditAccountCtx } from "./StoreCreditAccountForm";

export const FormCreditAccountModal: FunctionComponent<
  React.PropsWithChildren<{
    isOpen: boolean;
    onClose: () => void;
    clientId?: number;
    brandId?: number;
    title: string;
    isEditForm?: boolean;
    primaryAction: {
      title: string;
      action: () => void;
      loading: boolean;
    };
  }>
> = observer(function ModalEditCreditAccount({
  clientId,
  brandId,
  isEditForm,
  title,
  primaryAction,
  isOpen,
  onClose,
}) {
  const store = useContext(FormCreditAccountCtx);

  const getBrandOptions = () => {
    if (clientId) {
      return store.brandOptions.filter((option) => {
        return option.client_id === clientId;
      });
    }

    return store.brandOptions;
  };

  useEffect(() => {
    if (brandId) {
      store.form.brand_id.onChange(brandId.toString());
    }
  }, []);

  const canSetBudgetCategories = can("brandCreditAccount_setBudgetCategories");

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" trapFocus={false}>
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormRow title={<Trans>Brand</Trans>}>
            <Select
              asPortal
              id="field-credit-account-brand"
              label={""}
              isDisabled={isEditForm}
              {...fieldToInputProps(store.form.brand_id)}
              options={getBrandOptions()}
              placeholder={t`Select brand`}
            />
          </FormRow>
          <FormRow title={<Trans>Title</Trans>}>
            <TextField
              id="field-credit-account-title"
              {...fieldToInputProps(store.form.title)}
              type="text"
            />
          </FormRow>
          <FormRow title={<Trans>Budget category</Trans>}>
            <Select
              asPortal
              isMulti
              isDisabled={!canSetBudgetCategories}
              isSearchable
              {...fieldToSelectProps(
                store.form.budget_category_ids,
                store.budgetOptionsWithDisabledState,
              )}
            />
          </FormRow>
          <FormRow title={<Trans>Team</Trans>}>
            <Select
              asPortal
              id="field-user-team"
              {...fieldToSelectProps(store.form.team_id, store.teamOptions)}
              placeholder={t`Select team`}
              isDisabled={!canSetBudgetCategories}
            />
          </FormRow>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="grey" onClick={onClose} variant="outline">
            <Trans>Cancel</Trans>
          </Button>
          <Button
            ml={3}
            isLoading={primaryAction.loading}
            onClick={primaryAction.action}
          >
            {primaryAction.title}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
